import { SectionHeader } from '../common/section-header'
import { personLabel } from '../common/person-label.js'
import { FormalNameFields } from '../common/identity-fields.js'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  Notice,
} from '@speaking-coach/ui'
import { PageHeader } from '../components/page-header'
import { OPERATOR_DETAIL_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { PendingSpinner, SaveButton } from '../common/save-button'
import { UnsavedChangesGuard } from '../common/unsaved-changes'
import { UndeliverableNotice } from '../email_suppressions/undeliverable-notice'
import { failureReasonProse, InviteStateCell } from '../students/invite-state-cell'
import { CohortAccessFields } from './cohort-access-fields'
import { adminHrUsersApi, messageForHrUserError } from './api'
import { HistoryLink } from '../activity/history-link'
import { editHrUserSchema, type EditHrUserForm } from './hr-user-schema'
import type { HrUser } from './types'

export function EditHrUserPage(): JSX.Element {
  const { clientId = '', userId = '' } = useParams()
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: queryKeys.hrUser(clientId, userId),
    queryFn: () => adminHrUsersApi.get(clientId, userId),
  })
  const access = useQuery({
    queryKey: queryKeys.hrUserCohortAccess(clientId, userId),
    queryFn: () => adminHrUsersApi.getCohortAccess(clientId, userId),
  })
  const form = useForm<EditHrUserForm>({
    resolver: zodResolver(editHrUserSchema),
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      cohortIds: [],
      hrAllCohortAccess: false,
    },
  })
  useEffect(() => {
    if (detail.data && access.data) {
      form.reset({
        firstName: detail.data.firstName ?? '',
        lastName: detail.data.lastName ?? '',
        cohortIds: access.data.cohortIds,
        hrAllCohortAccess: detail.data.hrAllCohortAccess,
      })
    }
  }, [access.data, detail.data, form])
  const allCohorts = form.watch('hrAllCohortAccess')
  // "Add all current cohorts" in flight: saving now would send the
  // selection from before the add (review finding on 780313ac).
  const [cohortsBusy, setCohortsBusy] = useState(false)

  const update = useMutation({
    mutationFn: async (values: EditHrUserForm) => {
      // Sequential, and the PATCH goes first: it carries the all-cohort
      // flag, and an OFF flip has to land before the replace-set below or
      // the server refuses that write as locked. (This used to be a
      // Promise.all — with the flag on, every name edit would have 409'd.)
      const updated = await adminHrUsersApi.update(clientId, userId, {
        firstName: values.firstName,
        lastName: values.lastName,
        hrAllCohortAccess: values.hrAllCohortAccess,
      })
      // While the flag is on the server owns the set and the PATCH response
      // already carries it; the replace-set is neither needed nor allowed.
      const updatedAccess = values.hrAllCohortAccess
        ? { cohortIds: updated.cohortIds }
        : await adminHrUsersApi.setCohortAccess(clientId, userId, values.cohortIds)
      return { updated, updatedAccess }
    },
    onSuccess: ({ updated, updatedAccess }) => {
      queryClient.setQueryData(queryKeys.hrUser(clientId, userId), updated)
      queryClient.setQueryData(
        queryKeys.hrUserCohortAccess(clientId, userId),
        updatedAccess,
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.client(clientId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.hrUsers })
      // Access rows moved, so every cohort's HR-access set and the cohort
      // lists' HR counts are stale — the client's cohort subtree covers the
      // per-cohort keys, the global list is its own root.
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientCohorts(clientId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cohorts })
      form.reset({
        firstName: updated.firstName ?? '',
        lastName: updated.lastName ?? '',
        cohortIds: updatedAccess.cohortIds,
        hrAllCohortAccess: updated.hrAllCohortAccess,
      })
    },
  })
  const resend = useMutation({
    mutationFn: () => adminHrUsersApi.resendInvite(clientId, userId),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.hrUser(clientId, userId), result)
      void queryClient.invalidateQueries({ queryKey: queryKeys.hrUsers })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.client(clientId),
        exact: true,
      })
    },
  })
  const inviteUser = detail.data

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_DETAIL_PAGE}>
        {(detail.isPending || access.isPending) && (
          <p className="text-text-secondary mt-4">Loading HR user…</p>
        )}
        {(detail.error || access.error) && (
          <Notice variant="danger">
            {messageForHrUserError(detail.error ?? access.error)}
          </Notice>
        )}
        {detail.data && access.data && (
          <>
            <PageHeader
              title={personLabel(detail.data) ?? 'HR user'}
              action={
                <HistoryLink
                  to={`/admin/clients/${clientId}/hr-users/${userId}/history`}
                />
              }
            />
            {detail.data.emailUndeliverable != null && (
              <div className="mb-6">
                <UndeliverableNotice value={detail.data.emailUndeliverable} />
              </div>
            )}
            {inviteUser && (
              <HrInviteSection
                hrUser={inviteUser}
                pending={resend.isPending}
                error={resend.error}
                onResend={() => resend.mutate()}
              />
            )}
            <section
              aria-label="Profile and access"
              className="bg-surface-card rounded-token-card p-6"
            >
              <form
                onSubmit={form.handleSubmit((values) => {
                  if (cohortsBusy) return
                  update.mutate(values)
                })}
                className="space-y-4"
                aria-label="Edit HR user"
                noValidate
              >
                <UnsavedChangesGuard
                  when={form.formState.isDirty}
                  name={`HR user ${personLabel(detail.data) ?? 'HR user'}`}
                />
                <SectionHeader title="Profile and access" />
                {update.error && (
                  <Notice variant="danger">
                    {messageForHrUserError(update.error)}
                  </Notice>
                )}
                <dl className="grid gap-3 rounded-token-card bg-surface-app p-3 text-body sm:grid-cols-2">
                  <div>
                    <dt className="text-text-secondary">Email</dt>
                    <dd>{detail.data.email}</dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Client</dt>
                    <dd>
                      <Link
                        to={`/admin/clients/${clientId}`}
                        className="underline"
                      >
                        {detail.data.clientName}
                      </Link>
                    </dd>
                  </div>
                </dl>
                <FormalNameFields
                  firstName={form.register('firstName')}
                  lastName={form.register('lastName')}
                  errors={form.formState.errors}
                />
                <Controller
                  control={form.control}
                  name="cohortIds"
                  render={({ field }) => (
                    <CohortAccessFields
                      clientId={clientId}
                      clientName={detail.data.clientName}
                      selected={field.value}
                      onChange={field.onChange}
                      allCohorts={allCohorts}
                      onAllCohortsChange={(value) =>
                        form.setValue('hrAllCohortAccess', value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      onBusyChange={setCohortsBusy}
                    />
                  )}
                />
                {cohortsBusy && (
                  <span role="status" className="text-caption text-text-secondary">
                    Waiting for the cohorts to finish loading…
                  </span>
                )}
                <SaveButton
                  pending={update.isPending}
                  dirty={form.formState.isDirty}
                  justSaved={update.isSuccess}
                  disabled={cohortsBusy}
                />
              </form>
            </section>
          </>
        )}
      </div>
    </main>
  )
}

function HrInviteSection({
  hrUser,
  pending,
  error,
  onResend,
}: {
  hrUser: HrUser
  pending: boolean
  error: unknown
  onResend: () => void
}): JSX.Element {
  const canResend = hrUser.inviteState !== 'activated'
  const button = (
    <Button type="button" variant="secondary" pending={pending} disabled={pending} onClick={onResend}>
      {pending ? <PendingSpinner surface="card" /> : <Icon name="resend" />}
      {pending ? 'Sending…' : 'Resend invitation'}
    </Button>
  )

  return (
    <section aria-label="Invitation" className="bg-surface-card rounded-token-card p-6 mb-4 space-y-3">
      <SectionHeader title="Invitation" meta={<InviteStateCell student={hrUser} />} />
      {hrUser.inviteState === 'pending' && (
        <p className="text-text-secondary text-body">
          Sent and waiting for this HR user to set a password. The current link
          expires{' '}
          {hrUser.inviteExpiresAt?.slice(0, 10) ?? 'on its configured date'}.
        </p>
      )}
      {hrUser.inviteState === 'failed' && (
        <Notice variant="warning">
          The invitation was not delivered.{' '}
          {hrUser.inviteSendFailedReason
            ? failureReasonProse(hrUser.inviteSendFailedReason)
            : 'Check the address, then resend.'}
        </Notice>
      )}
      {hrUser.inviteState === 'expired' && (
        <Notice variant="warning">
          The invitation expired before it was used. Resend to create a fresh link.
        </Notice>
      )}
      {hrUser.inviteState === 'activated' && (
        <p className="text-text-secondary text-body">
          This HR user has activated their account and set a password.
        </p>
      )}
      {error !== null && error !== undefined && (
        <Notice variant="danger">{messageForHrUserError(error)}</Notice>
      )}
      {hrUser.emailSent === true && (
        <p role="status" className="text-text-secondary text-body">
          New invitation sent. The previous link no longer works.
        </p>
      )}
      {canResend && hrUser.inviteState === 'pending' && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="secondary" pending={pending} disabled={pending}>
              {pending ? (
                <PendingSpinner surface="card" />
              ) : (
                <Icon name="resend" />
              )}
              {pending ? 'Sending…' : 'Resend invitation'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Replace the current invitation?</AlertDialogTitle>
              <AlertDialogDescription>
                The current link will stop working immediately, including if this HR user
                is already setting their password. Only the newest email link will work.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onResend}>
                Resend and replace link
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {canResend && hrUser.inviteState !== 'pending' && button}
    </section>
  )
}
